import Button from "@components/library/Button";
import { useMonitoringFile } from "@/context/MonitoringFileContext";
import { downloadFile, getTimestampForFileName, pluralise } from "lib/utils";
import AppDialog from "@components/library/AppDialog";
import { Datapoint, getDatasetWithDatapoints } from "@/types/app/dataset";
import { useEffect, useState } from "react";
import { DownloadSimple } from "@phosphor-icons/react";
import LoadingIndicator from "@components/library/LoadingIndicator";
import { hlToast, ToastVariant } from "@components/library/Toast";
import { createCSVBlobFromDatapoints } from "@components/library/utils/dataset-csv";

interface Props {
  open: boolean;
  onClose: () => void;
  versionId?: string;
  datapoints?: Datapoint[];
  datapointsCount: number;
}

export const DatapointsExportDialog = ({ datapoints, open, onClose, versionId, datapointsCount }: Props) => {
  const { file } = useMonitoringFile();
  const [processing, setProcessing] = useState(false);

  const handleOnClose = () => {
    setProcessing(false);
    onClose();
  };

  const fetchDatapoints = async () => {
    if (versionId && file?.id) {
      const dataset = await getDatasetWithDatapoints({ fileId: file?.id, versionId });
      return dataset?.datapoints;
    } else {
      return [];
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={handleOnClose}
      title={"Export as CSV"}
      description={
        datapointsCount > 0 ? (
          <div className="max-w-prose text-13-18 italic text-text-base-1">
            You will export{" "}
            <b>
              {datapointsCount} datapoint{pluralise(datapointsCount)}
            </b>
            .
          </div>
        ) : (
          <div className="max-w-prose text-13-18 italic text-text-base-1">No datapoints to export</div>
        )
      }
    >
      <div className="flex max-w-prose items-center justify-start gap-8 text-13-18">
        {file && (
          <Button
            onClick={async () => {
              setProcessing(true);
              try {
                const datapointsToExport = [];
                if (datapoints) {
                  datapointsToExport.push(...datapoints);
                } else {
                  const fetchedDatapoints = await fetchDatapoints();
                  if (fetchedDatapoints) {
                    datapointsToExport.push(...fetchedDatapoints);
                  }
                }
                const csvBlob = await createCSVBlobFromDatapoints(datapointsToExport);
                downloadFile(
                  csvBlob,
                  `${file.name}${versionId ? "-" + versionId : ""}-${getTimestampForFileName()}-inputs.csv`,
                  "application/csv",
                );
                handleOnClose();
                hlToast({
                  title: "Data exported",
                  description: "Your data has been exported successfully",
                  variant: ToastVariant.Success,
                });
              } catch (e: any) {
                hlToast({
                  title: "Error exporting data",
                  description: e.message,
                  variant: ToastVariant.Error,
                });
              } finally {
                setProcessing(false);
              }
            }}
            IconLeft={DownloadSimple}
            size={32}
            styling="solid"
            disabled={processing || datapointsCount === 0}
          >
            Download
          </Button>
        )}
        {processing && <LoadingIndicator className="h-16 w-16" />}
      </div>
    </AppDialog>
  );
};
